import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Edit, CheckCircle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function FamilyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);

  useEffect(() => {
    fetchFamily();
  }, [id]);

  const fetchFamily = async () => {
    try {
      const response = await api.get(`/families/${id}`);
      setFamily(response.data);
    } catch (error) {
      toast.error('Failed to fetch family data');
      navigate('/families');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelivery = async () => {
    setConfirmingDelivery(true);
    try {
      await api.post(`/families/${id}/confirm-delivery`);
      toast.success('Delivery confirmed successfully!');
      fetchFamily();
    } catch (error) {
      toast.error('Failed to confirm delivery');
    } finally {
      setConfirmingDelivery(false);
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="ml-64 flex-1 p-8">
          <div className="text-center py-12">Loading...</div>
        </div>
      </div>
    );
  }

  if (!family) return null;

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="family-detail-page">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/families')}
              className="mb-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Families
            </Button>
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-4xl font-bold text-foreground mb-2">
                  {family.parent_first_name} {family.parent_last_name}
                </h1>
                <Badge
                  data-testid="delivery-status-badge"
                  className={family.delivery_status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                >
                  {family.delivery_status}
                </Badge>
              </div>
              <div className="flex gap-2">
                {family.delivery_status === 'pending' && (
                  <Button
                    onClick={handleConfirmDelivery}
                    disabled={confirmingDelivery}
                    className="rounded-full"
                    data-testid="confirm-delivery-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {confirmingDelivery ? 'Confirming...' : 'Confirm Delivery'}
                  </Button>
                )}
                <Link to={`/families/edit/${id}`}>
                  <Button variant="outline" className="rounded-full" data-testid="edit-button">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <Card className="border-stone-200 shadow-card">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-stone-600">Email</p>
                    <p className="font-medium" data-testid="family-email">{family.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">Phone</p>
                    <p className="font-medium" data-testid="family-phone">{family.phone}</p>
                  </div>
                </div>
                {family.parent_role && (
                  <div>
                    <p className="text-sm text-stone-600">Parent Role</p>
                    <p className="font-medium" data-testid="family-role">{family.parent_role}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-stone-600">Address</p>
                  <p className="font-medium" data-testid="family-address">{family.address}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-stone-600">Location Type</p>
                    <p className="font-medium" data-testid="location-type">{family.location_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">Location Detail</p>
                    <p className="font-medium" data-testid="location-detail">{family.location_detail}</p>
                  </div>
                </div>
                {family.created_at && (
                  <div>
                    <p className="text-sm text-stone-600">Member Since</p>
                    <p className="font-medium" data-testid="member-since">
                      {new Date(family.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-card">
              <CardHeader>
                <CardTitle>Children ({family.children.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {family.children.map((child, index) => (
                  <div key={index} className="p-4 border border-stone-200 rounded-lg" data-testid={`child-${index}`}>
                    <h4 className="font-semibold mb-3">{child.name}</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-stone-600">Age:</span> <span className="font-medium">{child.age}</span>
                      </div>
                      <div>
                        <span className="text-stone-600">Gender:</span> <span className="font-medium">{child.gender}</span>
                      </div>
                      <div>
                        <span className="text-stone-600">Learning Stage:</span> <span className="font-medium">{child.learning_stage}</span>
                      </div>
                      <div>
                        <span className="text-stone-600">Reading Level:</span> <span className="font-medium">{child.reading_level}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-stone-600">Interests:</span> <span className="font-medium">{child.favorite_interests || 'None specified'}</span>
                      </div>
                      <div>
                        <span className="text-stone-600">Library Card:</span> <span className="font-medium">{child.has_library_card ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-card">
              <CardHeader>
                <CardTitle>Household Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-stone-600">Books in Home</p>
                    <p className="font-medium" data-testid="books-count">{family.books_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">Annual Income</p>
                    <p className="font-medium" data-testid="annual-income">{family.annual_income}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">Has Bookshelf</p>
                    <p className="font-medium" data-testid="has-bookshelf">{family.already_has_bookshelf ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">Books from Birth</p>
                    <p className="font-medium" data-testid="books-from-birth">{family.books_from_birth_signup ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">Library Card</p>
                    <p className="font-medium" data-testid="has-library-card">{family.has_library_card ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">SMS/Text Consent</p>
                    <p className="font-medium" data-testid="sms-consent">{family.sms_consent ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">Photo/Video Consent</p>
                    <p className="font-medium" data-testid="photo-video-consent">{family.photo_video_consent ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                {family.already_has_bookshelf && family.bookshelf_reason && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-medium mb-1">Why they want another bookshelf:</p>
                    <p className="text-sm text-amber-700">{family.bookshelf_reason}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {family.delivery_confirmed_at && (
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <CardTitle>Delivery Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-stone-600">Confirmed At</p>
                  <p className="font-medium" data-testid="delivery-date">
                    {new Date(family.delivery_confirmed_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}